import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import getConversationSortByProperty from 'owa-mail-triage-table-utils/lib/getConversationSortByProperty';
import onLoadInitialRowsSucceeded from '../onLoadInitialRowsSucceeded';
import RequestOptions from 'owa-service/lib/RequestOptions';
import {
    getBaseFolderId,
    getBaseServerFolderId,
    TableView,
    MailFolderTableQuery,
    MailSortHelper,
} from 'owa-mail-list-store';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { shouldTableSortByRenewTime } from 'owa-mail-list-response-processor';
import { INITIAL_LOAD_ROW_COUNT } from 'owa-mail-triage-table-utils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { TraceErrorObject } from 'owa-trace';
import {
    findConversationWithStartIndex,
    getFindConversationsRestrictions,
    getConversationRowsUsingGql,
} from 'owa-mail-find-rows';
import folderId from 'owa-service/lib/factory/folderId';
import FolderId from 'owa-service/lib/contract/FolderId';
import { apolloErrorToResponseCode } from 'owa-apollo-errors/lib/apolloErrorToResponseCode';
import { getMailboxRequestOptions } from 'owa-request-options-types';

/**
 * Load the initial conversations from the server
 * @param tableView to load conversations in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
export default function loadInitialConversationsFromServer(
    tableView: TableView,
    isTablePrefetched: boolean,
    onInitialTableLoadComplete: OnInitialTableLoadComplete
): Promise<void> {
    if (isFeatureEnabled('mon-conv-useGqlForFindConv')) {
        return loadInitialConversationsUsingGql(
            tableView,
            isTablePrefetched,
            onInitialTableLoadComplete
        );
    } else {
        return loadInitialConversationsUsingFindConversation(
            tableView,
            isTablePrefetched,
            onInitialTableLoadComplete
        );
    }
}

/**
 * Load the initial conversations from the server using FindConversation directly
 * @param tableView to load conversations in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
function loadInitialConversationsUsingFindConversation(
    tableView: TableView,
    isTablePrefetched: boolean,
    onInitialTableLoadComplete: OnInitialTableLoadComplete
): Promise<void> {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
    const isFilter = mailTableQuery.viewFilter && mailTableQuery.viewFilter !== 'All';
    const { sortColumn, sortDirection } = MailSortHelper.getDefaultSortBy();
    const isSort =
        mailTableQuery.sortBy &&
        (mailTableQuery.sortBy.sortColumn !== sortColumn ||
            mailTableQuery.sortBy.sortDirection !== sortDirection);

    let requestOptions: RequestOptions =
        getMailboxRequestOptions(getMailboxInfoFromTableQuery(mailTableQuery)) || {};
    requestOptions.datapoint = {
        customData: {
            scenarioType: mailTableQuery.scenarioType,
            isPrefetch: isTablePrefetched,
            isFilter: isFilter,
            isSort: isSort,
            isRoamingFavoritesEnabled: isFeatureEnabled('tri-favorites-roaming'), // https://msfast.visualstudio.com/FAST/_workitems/edit/260700: Remove this line once flight is 100% WW
        },
    };

    const findConversationRestrictions = getFindConversationsRestrictions(tableView);

    const findConversationResponse = findConversationWithStartIndex(
        getBaseFolderId(mailTableQuery),
        0 /* startIndex */,
        INITIAL_LOAD_ROW_COUNT,
        mailTableQuery.viewFilter,
        getConversationSortByProperty(mailTableQuery),
        mailTableQuery.focusedViewFilter,
        mailTableQuery.requestShapeName,
        mailTableQuery.shouldTryToUseSessionDataResponse,
        requestOptions,
        mailTableQuery.categoryName,
        getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */),
        findConversationRestrictions /* restriction required for static folders */,
        findConversationRestrictions /* refinerRestriction  required for search folders */
    );

    return processFindConversationResponse(
        findConversationResponse,
        tableView,
        onInitialTableLoadComplete,
        isTablePrefetched
    );
}

function processFindConversationResponse(
    findConversation: Promise<any>,
    tableView: TableView,
    onInitialTableLoadComplete: OnInitialTableLoadComplete,
    isTablePrefetched: boolean
): Promise<void> {
    return findConversation.then(
        (response: {
            findConversationResponseMessage: FindConversationResponseMessage;
            responseInit: RequestOptions;
            url: string;
        }) => {
            let promiseToReturn = Promise.resolve();
            const responseMessage = response.findConversationResponseMessage;

            const isSuccessResponseClass = responseMessage.ResponseClass == 'Success';
            if (isSuccessResponseClass) {
                onLoadInitialRowsSucceeded(
                    tableView,
                    responseMessage.Conversations,
                    responseMessage.TotalConversationsInView,
                    responseMessage.SearchFolderId,
                    responseMessage.FolderId
                );
            } else {
                const error: TraceErrorObject = new Error(
                    `ResponseCode=${responseMessage.ResponseCode}`
                );

                error.fetchErrorType = 'ServerFailure';
                error.response = new Response(undefined, <ResponseInit>response.responseInit);
                promiseToReturn = Promise.reject(error);
            }

            onInitialTableLoadComplete(
                tableView,
                isSuccessResponseClass,
                responseMessage.ResponseCode,
                isTablePrefetched
            );

            return promiseToReturn;
        },
        error => {
            // Server returned non-200 response
            const responseCode = error?.response?.status || '';
            onInitialTableLoadComplete(tableView, false, responseCode, isTablePrefetched);
            return Promise.reject(error);
        }
    );
}

/**
 * Load the initial conversation rows from the server using the GraphQL query
 * @param tableView to load conversations in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
function loadInitialConversationsUsingGql(
    tableView: TableView,
    isTablePrefetched: boolean,
    onInitialTableLoadComplete: OnInitialTableLoadComplete
): Promise<void> {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
    const serverBaseFolderId = getBaseServerFolderId(
        tableView,
        true /* returnNullIfSameAsFolderId */
    );
    const serverFolderId = (serverBaseFolderId as FolderId)?.Id;

    const isFilter = mailTableQuery.viewFilter && mailTableQuery.viewFilter !== 'All';
    const { sortColumn, sortDirection } = MailSortHelper.getDefaultSortBy();
    const isSort =
        mailTableQuery.sortBy &&
        (mailTableQuery.sortBy.sortColumn !== sortColumn ||
            mailTableQuery.sortBy.sortDirection !== sortDirection);

    return getConversationRowsUsingGql(
        mailTableQuery.folderId,
        INITIAL_LOAD_ROW_COUNT,
        mailTableQuery.viewFilter,
        mailTableQuery.sortBy,
        shouldTableSortByRenewTime(mailTableQuery),
        mailTableQuery.focusedViewFilter,
        mailTableQuery.requestShapeName,
        getMailboxInfoFromTableQuery(mailTableQuery),
        undefined, // lastInstanceKey, so load from start of folder
        mailTableQuery.categoryName,
        serverFolderId,
        {
            datapoint: {
                customData: {
                    scenarioType: mailTableQuery.scenarioType,
                    isPrefetch: isTablePrefetched,
                    isFilter: isFilter,
                    isSort: isSort,
                },
            },
        }, // requestOptions
        mailTableQuery.shouldTryToUseSessionDataResponse
    )
        .then(responseMessage => {
            let conversationTypes = [];
            if (responseMessage.edges) {
                conversationTypes = responseMessage.edges.map(edge => edge.node);
            }

            onLoadInitialRowsSucceeded(
                tableView,
                conversationTypes,
                responseMessage.totalConversationRowsInView,
                responseMessage.searchFolderId
                    ? folderId({ Id: responseMessage.searchFolderId })
                    : null,
                responseMessage.folderId ? folderId({ Id: responseMessage.folderId }) : null
            );

            onInitialTableLoadComplete(
                tableView,
                true, // isSuccessResponseClass
                '200',
                isTablePrefetched
            );

            return Promise.resolve();
        })
        .catch((error: any) => {
            const responseCode = apolloErrorToResponseCode(error);
            onInitialTableLoadComplete(tableView, false, responseCode, isTablePrefetched);
            return Promise.reject(error);
        });
}
